import React, { useState } from 'react';
import { Button, Form, Input, Typography, message } from 'antd';
import { MailOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useJoinWaitlistMutation } from '../../services/nestjs-base-api';

const { Text, Title } = Typography;

interface WaitlistStepProps {
  brandName: string;
  websiteUrl?: string;
  productCategory?: string;
  businessModel?: string;
  isMobile: boolean;
  prefill?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export const WaitlistStep: React.FC<WaitlistStepProps> = ({
  brandName,
  websiteUrl,
  productCategory,
  businessModel,
  isMobile,
  prefill,
}) => {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);
  const [joinWaitlist, joinWaitlistResult] = useJoinWaitlistMutation();
  const [messageApi, contextHolder] = message.useMessage();

  const handleSubmit = async (values: { firstName: string; lastName: string; email: string }) => {
    const result = await joinWaitlist({
      payload: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        businessName: brandName,
        websiteUrl: websiteUrl || undefined,
        productCategory: productCategory || undefined,
        businessModel: businessModel || undefined,
      },
    });

    if ('data' in result) {
      setSubmitted(true);
    } else {
      messageApi.error(intl.formatMessage({ id: 'auth.signup.registrationError' }));
    }
  };

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: isMobile ? '2rem 0' : '3rem 0' }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem',
          boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)',
        }}>
          <CheckCircleOutlined style={{ fontSize: '40px', color: 'white' }} />
        </div>
        <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '1rem', fontSize: isMobile ? '24px' : '30px' }}>
          {intl.formatMessage({ id: 'auth.waitlist.successTitle' })}
        </Title>
        <Text style={{ color: '#6b7280', fontSize: isMobile ? '15px' : '16px', lineHeight: '1.6', display: 'block', marginBottom: '2rem' }}>
          {intl.formatMessage({ id: 'auth.waitlist.successDescription' })}
        </Text>
        <div style={{ textAlign: 'center' }}>
          <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
            {intl.formatMessage({ id: 'auth.signup.hasAccount' })}
          </Text>
          {' '}
          <Link to="/sign-in" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}>
            {intl.formatMessage({ id: 'auth.signup.signinLink' })}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
        <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '0.5rem', fontSize: isMobile ? '24px' : '30px' }}>
          {intl.formatMessage({ id: 'auth.waitlist.title' })}
        </Title>
        <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px', lineHeight: '1.6', display: 'block', marginBottom: '1rem' }}>
          {intl.formatMessage(
            { id: 'auth.waitlist.description' },
            {
              brandName,
              b: (chunks: React.ReactNode) => <strong style={{ color: '#1f2937' }}>{chunks}</strong>,
            },
          )}
        </Text>
        <Text style={{ color: '#374151', fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>
          {intl.formatMessage({ id: 'auth.waitlist.formDescription' })}
        </Text>
      </div>

      <Form
        name="waitlist_form"
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
        size="large"
        initialValues={{
          firstName: prefill?.firstName || '',
          lastName: prefill?.lastName || '',
          email: prefill?.email || '',
        }}
      >
        <div style={{ display: 'flex', gap: '12px' }}>
          <Form.Item
            name="firstName"
            label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.firstName' })}</span>}
            rules={[{ required: true, message: intl.formatMessage({ id: 'auth.waitlist.firstNameRequired' }) }]}
            style={{ flex: 1 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
              placeholder={intl.formatMessage({ id: 'auth.waitlist.firstNamePlaceholder' })}
              style={{ height: isMobile ? '52px' : '48px', borderRadius: '12px', border: '2px solid #e5e7eb', fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.lastName' })}</span>}
            rules={[{ required: true, message: intl.formatMessage({ id: 'auth.waitlist.lastNameRequired' }) }]}
            style={{ flex: 1 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
              placeholder={intl.formatMessage({ id: 'auth.waitlist.lastNamePlaceholder' })}
              style={{ height: isMobile ? '52px' : '48px', borderRadius: '12px', border: '2px solid #e5e7eb', fontSize: '16px' }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="email"
          label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.signup.label.email' })}</span>}
          rules={[{ type: 'email', required: true, message: intl.formatMessage({ id: 'auth.waitlist.emailRequired' }) }]}
        >
          <Input
            prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
            placeholder={intl.formatMessage({ id: 'auth.waitlist.emailPlaceholder' })}
            style={{ height: isMobile ? '52px' : '48px', borderRadius: '12px', border: '2px solid #e5e7eb', fontSize: '16px' }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            loading={joinWaitlistResult.isLoading}
            style={{
              height: isMobile ? '52px' : '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.4)',
            }}
          >
            {intl.formatMessage({ id: 'auth.waitlist.submitButton' })}
          </Button>
        </Form.Item>

        <div style={{ textAlign: 'center', marginTop: isMobile ? '1rem' : '1.5rem' }}>
          <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
            {intl.formatMessage({ id: 'auth.signup.hasAccount' })}
          </Text>
          {' '}
          <Link to="/sign-in" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}>
            {intl.formatMessage({ id: 'auth.waitlist.signinLink' })}
          </Link>
        </div>
      </Form>
    </>
  );
};
